import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BlogLayout } from "@/pages/blog/BlogLayout";
import { useSeo } from "@/pages/blog/useSeo";
import { MaskLine, Reveal } from "@/components/landing/Reveal";
import { WHATSAPP_URL, FINANCING_PATH } from "@/components/landing/data";
import { FIN_STEPS, FIN_FAQ, FIN_LEGAL, FIN, clampAmount } from "@/content/financing";
import { Simulator } from "./Simulator";
import { QuoteForm } from "./QuoteForm";

const JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FIN_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

const Hero = () => (
  <section className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-24 sm:pt-32 pb-14">
    <p className="eyebrow text-[#3E6B48]">Dilaziona il tuo preventivo</p>
    <h1 className="f-serif text-4xl sm:text-6xl lg:text-7xl text-[#1B3B28] mt-5 leading-[0.98] max-w-4xl" data-testid="financing-title">
      <MaskLine>Fino a 24 rate,</MaskLine>
      <MaskLine delay={0.12}><em className="text-[#3E6B48]">a interessi zero.</em></MaskLine>
    </h1>
    <Reveal delay={0.3}>
      <p className="text-base sm:text-lg text-[#526356] mt-8 max-w-2xl leading-relaxed">
        Hai ricevuto un preventivo e vuoi un secondo parere? È normale confrontarsi con diversi dentisti. Simula la rata,
        caricaci il preventivo e, se ce l'hai, la panoramica: ti daremo il nostro esito e una proposta, dilazionabile fino a 9.000 € con TAN 0% e TAEG 0%.
      </p>
    </Reveal>
  </section>
);

const Steps = () => (
  <section className="max-w-[1400px] mx-auto px-6 sm:px-10 py-20 sm:py-28 grid gap-5 md:grid-cols-3" data-testid="financing-steps">
    {FIN_STEPS.map((s, i) => (
      <motion.div key={s.n} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.7, delay: i * 0.08 }} className="rounded-3xl border border-[#1B3B28]/10 bg-[#F4F3EC] p-7 sm:p-8" data-testid={`financing-step-${s.n}`}>
        <span className="f-mono text-xs tracking-[0.2em] text-[#3E6B48]">{s.n}</span>
        <h2 className="f-serif text-3xl text-[#1B3B28] mt-8 leading-tight">{s.title}</h2>
        <p className="text-[#526356] mt-3 leading-relaxed">{s.text}</p>
      </motion.div>
    ))}
  </section>
);

const Done = ({ info }) => (
  <div className="rounded-3xl bg-[#E6ECE4] p-8 sm:p-10" data-testid="quote-success">
    <CheckCircle2 className="text-[#3E6B48]" size={32} />
    <h3 className="f-serif text-4xl text-[#1B3B28] mt-5 leading-tight">Grazie {info.name}, l'abbiamo ricevuto.</h3>
    <p className="text-[#526356] mt-4 leading-relaxed max-w-xl">
      Il Dott. Mauro valuterà il tuo preventivo con calma e ti ricontatteremo con il nostro esito e una proposta.
      {info.emailSent ? ` Ti abbiamo inviato una conferma a ${info.email}.` : ""}
    </p>
    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="quote-success-whatsapp"
      className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#1B3B28]/20 px-6 py-3.5 text-sm text-[#1B3B28] hover:bg-[#FCFBF7] transition-colors">
      <MessageCircle size={15} /> Hai fretta? Scrivici su WhatsApp
    </a>
  </div>
);

const Faq = () => (
  <section className="max-w-[1400px] mx-auto px-6 sm:px-10 py-20 sm:py-28 grid lg:grid-cols-12 gap-12" data-testid="financing-faq-section">
    <div className="lg:col-span-4">
      <p className="eyebrow text-[#3E6B48]">Domande frequenti</p>
      <h2 className="f-serif text-4xl sm:text-5xl text-[#1B3B28] leading-[1.02] mt-5">Nessun impegno, <em className="text-[#3E6B48]">solo chiarezza.</em></h2>
    </div>
    <Accordion type="single" collapsible className="lg:col-span-7 lg:col-start-6 border-t border-[#1B3B28]/15" data-testid="financing-faq">
      {FIN_FAQ.map((f, i) => (
        <AccordionItem key={f.q} value={`f-${i}`} className="border-b border-[#1B3B28]/15">
          <AccordionTrigger data-testid={`financing-faq-trigger-${i}`} className="f-serif text-xl sm:text-2xl text-left text-[#1B3B28] py-6 hover:no-underline hover:text-[#3E6B48]">{f.q}</AccordionTrigger>
          <AccordionContent className="text-base text-[#526356] leading-relaxed pb-6">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

export default function FinancingPage() {
  const [params] = useSearchParams();
  const [amount, setAmount] = useState(() => (params.get("importo") ? clampAmount(params.get("importo")) : 3000));
  const [rate, setRate] = useState(() => Math.min(FIN.maxRate, Math.max(FIN.minRate, parseInt(params.get("rate"), 10) || 12)));
  const [done, setDone] = useState(null);
  useSeo({
    title: "Dilaziona il tuo preventivo dentistico in 24 rate a interessi zero",
    description: "Simula la rata e carica il tuo preventivo con la panoramica: il Dott. Nicola Mauro ti dà un secondo parere e una proposta dilazionabile fino a 24 rate, TAN 0% TAEG 0%, fino a 9.000 €. Centro Dentistico Galliera.",
    path: FINANCING_PATH,
    jsonLd: JSONLD,
  });
  const toForm = () => document.getElementById("carica-preventivo")?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <BlogLayout>
      <div data-testid="financing-page">
        <Hero />
        <section className="max-w-[1400px] mx-auto px-4 sm:px-10">
          <Simulator amount={amount} setAmount={setAmount} rate={rate} setRate={setRate} onUse={toForm} />
          <p className="text-xs text-[#526356] mt-4 max-w-3xl leading-relaxed" data-testid="financing-legal-note">{FIN_LEGAL}</p>
        </section>
        <Steps />
        <section id="carica-preventivo" className="scroll-mt-28 bg-[#F4F3EC] py-20 sm:py-28" data-testid="financing-upload-section">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <p className="eyebrow text-[#3E6B48]">Carica il tuo preventivo</p>
              <h2 className="f-serif text-4xl sm:text-5xl text-[#1B3B28] leading-[1.02] mt-5">Un secondo parere, <em className="text-[#3E6B48]">con calma.</em></h2>
              <p className="text-[#526356] mt-5 leading-relaxed">Bastano una foto del preventivo e, se l'hai, della panoramica. Puoi anche fissare direttamente una <Link to="/prima-visita" className="underline underline-offset-4 text-[#1B3B28]" data-testid="financing-firstvisit-link">prima visita</Link>.</p>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              {done ? <Done info={done} /> : <QuoteForm amount={clampAmount(amount)} rate={rate} onDone={setDone} />}
            </div>
          </div>
        </section>
        <Faq />
      </div>
    </BlogLayout>
  );
}
